import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useTrainingFollow } from '@/hooks/useTrainingFeed';
import { UserPlus, UserCheck, Loader2 } from 'lucide-react';

interface TrainingFollowButtonProps {
  targetProfileId: string;
  className?: string;
  size?: 'sm' | 'default' | 'lg';
}

export const TrainingFollowButton: React.FC<TrainingFollowButtonProps> = ({
  targetProfileId,
  className,
  size = 'sm',
}) => {
  const { follow, unfollow, checkIsFollowing, isFollowing, isUnfollowing, currentUserId } = useTrainingFollow();
  const [isFollowed, setIsFollowed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const check = async () => {
      setLoading(true);
      const result = await checkIsFollowing(targetProfileId);
      setIsFollowed(result);
      setLoading(false);
    };
    
    if (currentUserId && targetProfileId !== currentUserId) {
      check();
    } else {
      setLoading(false);
    }
  }, [targetProfileId, currentUserId, checkIsFollowing]);

  // Don't show button for own content
  if (!currentUserId || targetProfileId === currentUserId) {
    return null;
  }

  const handleClick = () => {
    if (isFollowed) {
      unfollow(targetProfileId);
      setIsFollowed(false);
    } else {
      follow(targetProfileId);
      setIsFollowed(true);
    }
  };

  if (loading) {
    return (
      <Button variant="outline" size={size} disabled className={className}>
        <Loader2 className="h-4 w-4 animate-spin" />
      </Button>
    );
  }

  const isPending = isFollowing || isUnfollowing;

  return (
    <Button
      variant={isFollowed ? 'secondary' : 'outline'}
      size={size}
      onClick={handleClick}
      disabled={isPending}
      className={className}
    >
      {isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : isFollowed ? (
        <>
          <UserCheck className="h-4 w-4 mr-1" />
          Following
        </>
      ) : (
        <>
          <UserPlus className="h-4 w-4 mr-1" />
          Follow
        </>
      )}
    </Button>
  );
};
